import React, {
  ReactNode,
  RefObject,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import MuiDialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {
  Breakpoint,
} from '@mui/material/styles';
import {
  Button,
} from '@/components/Button';

export type Part = 'dialog' | 'container' | 'bodyRoot' | 'root' | 'title' | 'close' | 'footer';

type Props = { // @TODO: use MUI DialogProps with this props
  className?: string;
  dialogClassName?: string;
  closeBtnClasses?: string; // @TODO: rework this param
  title?: string | ReactNode;
  // @TODO: move all className props to classes
  titleClassName?: string;
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string; // @TODO: rework this param
  cancelText?: string;
  disableBackdropClick?: boolean;
  disableConfirmButton?: boolean;
  disableEscapeKeyDown?: boolean;
  showActions?: boolean; // @TODO: rework this param
  hideCloseBtn?: boolean; // @TODO: rework this param
  headerActions?: ReactNode;
  content?: ReactNode;
  children?: ReactNode;
  maxWidth?: false | Breakpoint | undefined;
  fullWidth?: boolean;
  dialogRef?: RefObject<HTMLDivElement>;
  minHeight?: number | string;
  disableEnforceFocus?: boolean;
  classes?: Partial<Record<Part, string>>;
  // autoFocusOptions?: AutoFocusOptions;
};

export default function Dialog({
  className,
  dialogClassName,
  closeBtnClasses,
  titleClassName,
  title,
  open,
  onClose,
  onConfirm = () => {},
  disableBackdropClick,
  disableConfirmButton,
  disableEscapeKeyDown = false,
  confirmText = 'Proceed',
  cancelText = 'Close',
  content,
  children,
  showActions,
  hideCloseBtn,
  headerActions,
  maxWidth = 'xs',
  fullWidth = false,
  dialogRef,
  minHeight: _minHeight,
  disableEnforceFocus,
  classes,
  // autoFocusOptions = {},
}: Props) {
  const rootClassName = clsx(
    'p-5 flex flex-col space-y-5 w-[24rem] relative text-sm nw-confirmation-dialog',
    className,
    classes?.root,
  );
  const dialogClasses = clsx(
    'populate-dialog',
    dialogClassName,
    classes?.dialog,
  );
  const header = typeof title === 'string'
    ? <h2 className="text-lg font-semibold mb-3">{title}</h2>
    : title;
  const minHeight = typeof _minHeight === 'number' ? `${_minHeight}px` : _minHeight;

  // useFormAutoFocus({
  //   ...autoFocusOptions,
  //   enabled: open && autoFocusOptions?.enabled,
  // });

  return (
    <MuiDialog
      className={dialogClasses}
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' && disableBackdropClick) return;
        if (reason === 'escapeKeyDown' && disableEscapeKeyDown) return;
        onClose();
      }}
      classes={{
        paper: clsx('!overflow-y-visible', classes?.bodyRoot),
        container: classes?.container,
      }}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEnforceFocus={disableEnforceFocus}
    >
      <div
        className={rootClassName}
        ref={dialogRef}
        style={!isNil(minHeight) ? { minHeight } : undefined}
      >
        {(!isEmpty(title) || !hideCloseBtn || !isNil(headerActions)) && (
          <div
            className={clsx('flex flex-row justify-between', titleClassName, classes?.title)}
          >
            {header}
            {!isNil(headerActions) && headerActions}
            {!hideCloseBtn && (
              <IconButton
                className={clsx('relative -top-3 -right-3 !w-8 !h-8 nw-close-button', closeBtnClasses, classes?.close)}
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            )}
          </div>
        )}
        {children ?? content}
        {showActions && (
          <div className={clsx('flex gap-4 items-center', classes?.footer)}>
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              className="!max-w-[unset] !min-w-[unset] flex-1"
            >
              {cancelText}
            </Button>
            <Button
              variant="default"
              type="submit"
              onClick={onConfirm}
              disabled={disableConfirmButton}
              className="!max-w-[unset] !min-w-[unset] flex-1"
            >
              {confirmText}
            </Button>
          </div>
        )}
      </div>
    </MuiDialog>
  );
}
